import VirtualScroll from 'virtual-scroll';

import { GetBy } from '../core/Element';
import { Maths } from '../utils/Maths';
import { CSS } from '../utils/CSS';
import Keyboard, { KEYS } from '../core/Keyboard';
import InfiniteScroll__Item from './InfiniteScroll__Item';
import { isMobile } from '../core/Basics';

export default class InfiniteScroll {
    static AXIS_X = 'X';
    static AXIS_Y = 'Y';

    scroller;
    direction = 1;

    position = 0;
    target = 0;
    speed = 0;
    total_items = 0;

    id;
    width;
    height;
    options;

    _container;
    _items = [];

    _enabled = false;
    _isShow = false;

    _axis = 'y';
    _measure = 'height';
    _domAxis = 'top';
    _offsetAxis = 'offsetTop';
    _offsetSize = 'offsetHeight';

    _call;

    //
    // GETTER & SETTER
    //
    get enabled() { return this._enabled; };
    set enabled(__isEnabled) {
        if (this._enabled !== __isEnabled) {
            this._enabled = __isEnabled;

            if (__isEnabled) {
                this.scroller.on(this._call);
            } else {
                this.scroller.off(this._call);
            }
        }
    };

    constructor(__axis, opts = {}) {
        this._container = opts.container || document.body;
        this._appendContainer = opts.appendContainer || this._container;
        this.width = this._container.offsetWidth;
        this.height = this._container.offsetHeight;

        this.options = {
            axis: __axis || 'Y',
            smooth: opts.smooth || false,
            easing: opts.easing || 0.08,
            maxSpeed: opts.maxSpeed || 400,
            multiplicator: opts.multiplicator || 1,
            itemClass: opts.itemClass || InfiniteScroll__Item,
            offset: opts.offset || 100
        };

        this.scroller = new VirtualScroll({
            mouseMultiplier: navigator.platform.indexOf('Win') > -1 ? 1 : 0.5,
            firefoxMultiplier: 50,
            touchMultiplier: 3.5,
            passive: true
        });

        this._call = (e) => { this.check(e) };

        this._container.classList.add('__vscroll-inifinity');

        switch (this.options.axis) {
            case InfiniteScroll.AXIS_Y:
                this._container.classList.add('__scroll-infinity-axis-y');
                this._axis = 'y';
                this._measure = 'height';
                this._domAxis = 'top';
                this._offsetAxis = 'offsetTop';
                this._offsetSize = 'offsetHeight';
                break;

            case InfiniteScroll.AXIS_X:
                this._container.classList.add('__scroll-infinity-axis-x');
                this._axis = 'x';
                this._measure = 'width';
                this._domAxis = 'left';
                this._offsetAxis = 'offsetLeft';
                this._offsetSize = 'offsetWidth';
                break;
        }

        this.setupResize(opts.domResize || this._container);
        this.addAll(opts.selector);
    }

    addAll(__selector = '[scroll-infinity-item]') {
        const items = GetBy.selector(__selector, this._container);
        for (let i = 0; i < items.length; i++) {
            const item = new this.options.itemClass(items[i], i, this._axis == 'y' ? InfiniteScroll.AXIS_Y : InfiniteScroll.AXIS_X);
            this.total_items = this._items.push(item);
        }
    }

    setupResize(__dom) {
        this.resizeObserver = new ResizeObserver(entries => {
            this.resize();
        });

        this.resizeObserver.observe(__dom);
    }

    //
    // PUBLIC
    //
    start() {
        this.enabled = true;
    }

    show() {
        if (!this._isShow) {
            this.loop(true);
            this._isShow = true;
        }
    }

    hide() {
        this.enabled = false;
        this._container.classList.remove('__vscroll-inifinity');
        this._container.classList.remove('__scroll-infinity-axis-y');
        this._container.classList.remove('__scroll-infinity-axis-x');
    }

    check(e) {
        const delta = isMobile ? e.deltaX : e.deltaY;

        const d = Maths.precission(delta * this.options.multiplicator, 2);
        this.target += d;
        this.direction = delta < 0 ? 1 : -1;
    }

    loop(__force = false) {
        if (Maths.precission(this.target) !== Maths.precission(this.position) || __force) {
            this.checkItemsReorder();
            this.calcSpeed();
            this.updateItemsPosition();
        }
    }

    checkItemsReorder() {
        if (this.direction > 0) {
            for (let i = 0; i < this.total_items; i++) {
                const item = this._items[i];
                const itemBottom = item[this._axis] + item[this._domAxis] + item[this._measure];

                if (itemBottom < -this.options.offset) {
                    this.updatePosition(item[this._measure]);
                    this.reorderItems(item, i);
                    break;
                }
            }
        } else {
            for (let i = this.total_items - 1; i > 0; i--) {
                const item = this._items[i];
                const itemTop = item[this._axis] + item[this._domAxis];

                if (itemTop > this[this._measure] + this.options.offset) {
                    this.updatePosition(-item[this._measure]);
                    this.reorderItems(item, i);
                    break;
                }
            }
        }
    }

    calcSpeed() {
        this.speed = (this.target - this.position) * this.options.easing;

        if (this.speed === 0) this.position = this.target;
        else this.position += this.speed;
    }

    updateItemsPosition() {
        for (let i = 0; i < this.total_items; i++) {
            this._items[i][this._axis] = this.position;
        }
    }

    updatePosition(__pos) {
        this.target += __pos;
        this.position += __pos;
    }

    reorderItems(__item, __i) {
        if (this.direction > 0) {
            this._appendContainer.appendChild(__item.dom);
            this._items.splice(__i, 1);
            this._items.push(__item);
        } else {
            this._appendContainer.prepend(__item.dom);
            this._items.splice(__i, 1);
            this._items.unshift(__item);
        }

        let newPos = 0;
        for (let j = 0; j < this._items.length; j++) {
            const currentItem = this._items[j];
            currentItem[this._domAxis] = newPos;
            newPos += currentItem[this._measure];
            currentItem.resizeLimits();
        }
    }

    resize() {
        this.width = this._container.offsetWidth;
        this.height = this._container.offsetHeight;

        if (this._isShow) this.loop(true);

        for (let i = 0; i < this.total_items; i++) this._items[i].resize(this._appendContainer[this._offsetSize]);
    }

    dispose() {
        this.enabled = false;

        for (let i = 0; i < this.total_items; i++) this._items[i].dispose();

        this.total_items = 0;
        this._items = [];
        this.scroller.destroy();

        this.resizeObserver.disconnect();
    }
}
